'use client';

import React, { useState, useEffect } from 'react';
import { Avatar, Input } from 'antd';
import { RightOutlined, ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { getSvcCategoryProducts } from '../../../services/api';

interface ProductData {
    id: number;
    name?: string;
    display?: string;
    code?: string;
    logo_url?: string | null;
    [key: string]: any;
}

interface ProductSelectionProps {
    svcCatCode: string;
    workspaceName: string;
    onBack: () => void;
    onProductSelect: (productData: ProductData) => void;
}

const ProductSelection: React.FC<ProductSelectionProps> = ({
    svcCatCode,
    workspaceName,
    onBack,
    onProductSelect
}) => {
    const [products, setProducts] = useState<ProductData[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<ProductData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<ProductData | null>(null);

    useEffect(() => {
        fetchProducts();
    }, [svcCatCode]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && selectedProduct) {
                handleProceed();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedProduct]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await getSvcCategoryProducts(svcCatCode);
            if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
                setProducts(response.data);
                setFilteredProducts(response.data);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDisplayName = (product: ProductData) =>
        product.display || product.name || product.code || String(product.id);

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        const filtered = products.filter(p =>
            getDisplayName(p).toLowerCase().includes(value.toLowerCase())
        );
        setFilteredProducts(filtered);
    };

    const handleProceed = () => {
        if (selectedProduct) {
            onProductSelect(selectedProduct);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white rounded-2xl shadow-sm flex flex-col h-[600px]">
            <div className="px-6 pt-6 pb-4">
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-[#F9FAFB] rounded-lg transition-colors"
                    >
                        <ArrowLeftOutlined className="text-[#667085]" />
                    </button>
                    <div>
                        <h1 className="text-[28px] font-semibold text-[#101828] mb-1">
                            {workspaceName}
                        </h1>
                        <p className="text-base text-[#475467]">
                            {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
                        </p>
                    </div>
                </div>

                <div className="mb-4">
                    <Input
                        prefix={<SearchOutlined className="text-[#667085]" />}
                        placeholder="Search"
                        onChange={(e) => handleSearch(e.target.value)}
                        value={searchQuery}
                        className="w-full py-2.5 px-3.5 rounded-lg border border-[#D0D5DD]"
                    />
                </div>
            </div>

            <div className="px-4 space-y-1 flex-1 overflow-y-auto">
                {filteredProducts.map((product) => (
                    <div
                        key={product.id}
                        className={`group cursor-pointer rounded-lg transition-all duration-200 hover:bg-[#F9FAFB] ${
                            selectedProduct?.id === product.id ? 'bg-[#F9FAFB]' : ''
                        }`}
                        onClick={() => setSelectedProduct(product)}
                    >
                        <div className="flex items-center justify-between py-2.5 px-2">
                            <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                    <Avatar
                                        src={product.logo_url || '/assets/login/company-default.svg'}
                                        size={32}
                                        className="bg-[#F4EBFF]"
                                        style={{ border: '1px solid #F9F5FF' }}
                                    />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#101828]">
                                        {getDisplayName(product)}
                                    </p>
                                    {product.code && product.display && (
                                        <p className="text-xs text-[#667085]">{product.code}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {product.total_count !== undefined && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-[#F4EBFF] text-[#6941C6]">
                                        {product.total_count}
                                    </span>
                                )}
                                <RightOutlined className="text-[#667085]" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="p-4 border-t border-[#EAECF0] bg-white sticky bottom-0">
                <button
                    onClick={handleProceed}
                    disabled={!selectedProduct}
                    className={`w-full py-2.5 px-4 rounded-lg text-sm font-semibold transition-colors ${
                        selectedProduct
                            ? 'bg-[#7F56D9] text-white hover:bg-[#6941C6]'
                            : 'bg-[#F2F4F7] text-[#D0D5DD] cursor-not-allowed'
                    }`}
                >
                    Proceed to login
                </button>
            </div>
        </div>
    );
};

export default ProductSelection;
